import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, AlertTriangle, Check, RotateCw } from 'lucide-react'
import { useI18n } from '../i18n'
import { useAuth } from '../auth'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { DEMO_AI_EXAMPLES, demoNotice } from '../lib/aiBudget'
import { Button, Card, cx } from '../ui/kit'
import { kaufwegSichtbar } from '../lib/kaufweg'

/**
 * Was die KI-Funktionen diesen Monat gekostet haben.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM ES DIESE KARTE GIBT.
 *
 * Ein Vermieter, der ohne Vorwarnung vor einer gesperrten Funktion steht, ruft
 * an. Einer, der ohne Vorwarnung eine Rechnung bekommt, ruft lauter an. Beides
 * ist vermeidbar, und zwar nur hier: der Verbrauch entsteht verteilt über den
 * Monat in Funktionen, die von sich aus nichts über Geld sagen.
 *
 * DREI ZAHLEN, IN DIESER REIHENFOLGE:
 *
 *   1. Was vom Inklusivbetrag verbraucht ist — die Zahl, die er sucht.
 *   2. Was darüber hinaus angefallen ist — die Zahl, die ihn überraschen würde.
 *   3. Wofür — „1,80 € für Textvorschläge, 0,90 € für Anfragen auslesen"
 *      beantwortet die Rückfrage, die sonst zum Anruf führt.
 *
 * WAS HIER NICHT STEHT: der Einkaufswert. Alle Beträge auf dieser Karte sind
 * VERRECHNETE Beträge — das, was es den Vermieter kostet. Was AUVISO dafür bei
 * Anthropic zahlt, steht in `ai_usage` und in der Betreibersicht
 * `ai_billing_health`, aber nicht hier. Die Marge geht ihn nichts an.
 *
 * WARUM DER DECKEL NICHT VERSTELLBAR IST: er ist eine kaufmännische Vorgabe von
 * AUVISO und keine Einstellung des Betriebs. Die Karte sagt das ausdrücklich —
 * ein Regler, den man sucht und nicht findet, ist ärgerlicher als ein Satz, der
 * erklärt, warum es keinen gibt.
 *
 * DIE EINZIGE ENTSCHEIDUNG, DIE HIER GETROFFEN WIRD, IST SEINE: ob über den
 * Inklusivbetrag hinaus weitergearbeitet und nachverrechnet werden darf. Sie
 * wird EINMAL getroffen, ausdrücklich, mit dem Betrag daneben — niemand läuft
 * durch bloßes Weiterklicken in eine Rechnung. Und sie ist widerrufbar.
 *
 * Texte inline statt in `locales/`, wie bei ComplianceCards und IcalCard: viel
 * Prosa, kurze Lebenserwartung in dieser Form, und vierzig Schlüssel im
 * gemeinsamen Katalog kosten mehr, als sie sparen.
 * ═══════════════════════════════════════════════════════════════════════════
 */

interface PurposeRow {
  purpose: string
  billed_micro_cent: number
  calls: number
}

interface Status {
  /** Der GELTENDE Inklusivbetrag — je nach Abonnementstatus der Test- oder der bezahlte. */
  cap_cent: number
  trial_cap_cent: number
  paid_cap_cent: number
  in_trial: boolean
  billed_cent: number
  included_cent: number
  overage_cent: number
  calls: number
  by_purpose: PurposeRow[]
  projected_cent: number
  resets_at: string
  over_included: boolean
  consent_granted_at: string | null
  billable: boolean
}

/**
 * Die Zwecke in Klartext. Unbekannte Schlüssel fallen unverändert durch — ein
 * roher Wert, den der Vermieter in eine Supportmail kopieren kann, ist besser
 * als ein höfliches „Sonstiges", das nichts sagt.
 */
function purposeLabel(key: string, de: boolean): string {
  const map: Record<string, [string, string]> = {
    text_suggestion: ['Textvorschläge', 'Text suggestions'],
    enquiry_parse: ['Anfragen auslesen', 'Reading enquiries'],
    price_advice: ['Preisvorschläge', 'Price suggestions'],
  }
  const hit = map[key]
  return hit ? (de ? hit[0] : hit[1]) : key
}

export default function AiUsageCard() {
  const { lang } = useI18n()
  const { tenantId, session } = useAuth()
  const store = useStore()
  const de = lang === 'de'

  const [status, setStatus] = useState<Status | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)

  const money = useCallback(
    (cent: number) => new Intl.NumberFormat(de ? 'de-AT' : 'en-GB', {
      style: 'currency', currency: 'EUR',
    }).format(cent / 100),
    [de],
  )

  const load = useCallback(async () => {
    if (!supabase || !tenantId) return
    setError(null)
    const { data, error: e } = await supabase.rpc('ai_budget_status', { p_tenant: tenantId })
    if (e) { setError(e.message); return }
    setStatus(data as Status)
  }, [tenantId])

  useEffect(() => {
    if (!store.live || !supabase || !tenantId) return
    void load()
    // Die Rolle entscheidet, ob der Zustimmungsknopf überhaupt erscheint. Nur der
    // Inhaber kann den Betrieb in eine wiederkehrende Rechnung schreiben; einem
    // Mitarbeiter einen Knopf zu zeigen, der ihm einen Fehler zurückgibt, wäre
    // schlechter als gar keiner.
    void supabase.from('memberships').select('role')
      .eq('tenant_id', tenantId).eq('user_id', session?.user.id ?? '')
      .maybeSingle()
      .then(({ data }) => setRole(data?.role ?? null))
  }, [store.live, tenantId, session?.user.id, load])

  const setConsent = async (granted: boolean) => {
    if (!supabase || !tenantId) return
    setBusy(true)
    setError(null)
    const { error: e } = await supabase.rpc('ai_set_overage_consent', {
      p_tenant: tenantId, p_granted: granted,
    })
    setBusy(false)
    setAsking(false)
    if (e) { setError(e.message); return }
    await load()
  }

  // Im Demomodus gibt es keinen Verbrauch, weil es keinen Server gibt. Die Karte
  // verschwindet trotzdem nicht: dass es eine Grenze GIBT, soll man auch in der
  // Vorschau erfahren.
  if (!store.live) {
    return (
      <Card className="p-4 space-y-3">
        <h2 className="font-medium flex items-center gap-2">
          <Sparkles size={16} className="text-accent" />
          {de ? 'KI-Verbrauch' : 'AI usage'}
        </h2>
        <p className="text-xs text-muted">
          {de
            ? 'In der Demo wird nichts verbraucht — und nichts gerechnet. Im eigenen Konto ist ein monatlicher Betrag im Preis enthalten; er steht dann an dieser Stelle, zusammen mit dem, was davon verbraucht ist.'
            : 'Nothing is used — and nothing is computed — in the demo. In your own account a monthly amount is included in the price; it appears here, together with what has been used.'}
        </p>
        <div className="rounded-xl bg-surface-2 p-3 space-y-2">
          {/* Seit dem Gastpost-Abbau (Vorgang 008) gibt es keine Textvorschläge
              mehr — beworben wird nur, was die App heute kann. */}
          <p className="text-xs font-medium">
            {de ? 'So sieht ein Preisvorschlag aus:' : 'This is what a price suggestion looks like:'}
          </p>
          <p className="text-xs whitespace-pre-line text-muted">
            {DEMO_AI_EXAMPLES.price_advice}
          </p>
          {/* Die Beschriftung ist nicht verhandelbar. Ein vorbereitetes Ergebnis,
              das sich als frisch gerechnet ausgibt, ist eine Täuschung — und der
              erste Eindruck von einem Produkt, dem der Interessent später seine
              Buchhaltung anvertrauen soll. */}
          <p className="text-[0.7rem] text-muted italic">{demoNotice(de)}</p>
        </div>
      </Card>
    )
  }

  if (!status) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted">
          {error ?? (de ? 'KI-Verbrauch wird geladen …' : 'Loading AI usage …')}
        </p>
      </Card>
    )
  }

  const resets = new Date(status.resets_at)
  const resetLabel = new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(resets)

  // Der Balken zeigt den INKLUSIVBETRAG, nicht den Gesamtverbrauch: er ist voll,
  // sobald die 3 € aufgebraucht sind, und was darüber liegt, steht als Zahl
  // daneben. Ein Balken, der über sein Ende hinausläuft, sagt nichts mehr.
  const fraction = status.cap_cent > 0
    ? Math.min(1, status.included_cent / status.cap_cent)
    : 1
  const consented = Boolean(status.consent_granted_at)
  const isOwner = role === 'owner'

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <Sparkles size={16} className="text-accent" />
        {de ? 'KI-Verbrauch' : 'AI usage'}
      </h2>

      <div>
        <p className="text-sm">
          {de ? 'Diesen Monat: ' : 'This month: '}
          <span className="font-medium tabular-nums">{money(status.included_cent)}</span>
          {de ? ' von ' : ' of '}
          <span className="tabular-nums">{money(status.cap_cent)}</span>
          {de ? ' inklusive' : ' included'}
        </p>
        <div className="mt-1.5 h-2 rounded-full bg-surface-2 overflow-hidden">
          <div
            className={cx('h-full rounded-full transition-all',
              status.over_included ? 'bg-warn' : 'bg-accent')}
            style={{ width: `${Math.round(fraction * 100)}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-muted tabular-nums">
          {status.calls > 0
            ? (de
              ? `${status.calls} Aufrufe · zurückgesetzt am ${resetLabel}`
              : `${status.calls} calls · resets on ${resetLabel}`)
            : (de ? `Noch nichts genutzt · Zeitraum bis ${resetLabel}`
                  : `Nothing used yet · period ends ${resetLabel}`)}
        </p>
      </div>

      {/* Der Überhang. Er steht in Warnfarbe, aber ohne Ausrufezeichen: es ist
          kein Fehler, sondern eine Rechnungsposition — und er hat ihr zugestimmt. */}
      {status.overage_cent > 0 && (
        <div className="rounded-xl bg-warn-soft text-warn px-3 py-2 text-xs space-y-1">
          <p className="font-medium tabular-nums">
            {de
              ? `Darüber hinaus: ${money(status.overage_cent)}`
              : `Beyond that: ${money(status.overage_cent)}`}
          </p>
          <p>
            {status.in_trial
              ? (de
                ? 'Im Test wird nichts davon verrechnet — die KI-Funktionen ruhen stattdessen, bis der Monat neu beginnt oder Sie auf das bezahlte Abo wechseln.'
                : 'Nothing of this is billed during the trial — the AI features pause instead, until the month resets or you switch to the paid plan.')
              : (de
                ? 'Dieser Betrag läuft über Ihre nächste AUVISO-Rechnung und wird über das hinterlegte Lastschriftmandat eingezogen.'
                : 'This amount goes onto your next AUVISO invoice and is collected via the direct debit mandate on file.')}
          </p>
        </div>
      )}

      {/* Aufschlüsselung nach Zweck. Nur wenn es etwas aufzuschlüsseln gibt —
          eine Liste mit einer Zeile ist keine Aufschlüsselung. */}
      {status.by_purpose.length > 1 && (
        <ul className="text-xs text-muted space-y-0.5">
          {status.by_purpose.map((p) => (
            <li key={p.purpose} className="flex justify-between gap-3">
              <span>{purposeLabel(p.purpose, de)}</span>
              <span className="tabular-nums">
                {money(Math.round(p.billed_micro_cent / 1_000_000))}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Die Hochrechnung, sobald sie etwas taugt. Am 1. wäre sie ein einzelner
          Tag mal 31 und damit eher Panikmache als Information. */}
      {status.calls > 0 && status.projected_cent > status.cap_cent && (
        <p className="text-xs text-muted tabular-nums">
          {de
            ? `Hochgerechnet auf den Monat: rund ${money(status.projected_cent)}`
            : `Projected for the full month: about ${money(status.projected_cent)}`}
        </p>
      )}

      {/* Die Entscheidung des Vermieters. */}
      {consented ? (
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-xs text-muted flex items-start gap-1.5">
            <Check size={14} className="text-ok shrink-0 mt-0.5" />
            <span>
              {de
                ? 'Sie haben zugestimmt, dass Verbrauch über dem Inklusivbetrag nachverrechnet wird.'
                : 'You have agreed that usage above the included amount is billed to you.'}
            </span>
          </p>
          {isOwner && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConsent(false)}>
              {de ? 'Zustimmung widerrufen' : 'Withdraw consent'}
            </Button>
          )}
        </div>
      ) : status.in_trial ? (
        /* TESTPHASE. Kein Nachkauf — aber auch keine Sackgasse: der Weg nach
           vorn ist das bezahlte Abo, und er steht mit der Zahl daneben, die
           dort gilt. Nirgends eine fest verdrahtete Summe. */
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-xs text-muted">
            {de
              /* In der Hülle bleibt die Auskunft über das Kontingent, aber ohne
                 den Verweis auf das bezahlte Abo und das Dazukaufen — beides
                 wäre eine Kaufaufforderung im Sinne von 3.1.3(f). */
              ? (kaufwegSichtbar()
                ? `Im Test sind ${money(status.trial_cap_cent)} KI je Monat enthalten. Mit dem Wechsel auf das bezahlte Abo stehen ${money(status.paid_cap_cent)} je Monat zur Verfügung, und weitere Leistungen lassen sich dazukaufen.`
                : `Im Test sind ${money(status.trial_cap_cent)} KI je Monat enthalten.`)
              : (kaufwegSichtbar()
                ? `The trial includes ${money(status.trial_cap_cent)} of AI per month. Switching to the paid plan gives you ${money(status.paid_cap_cent)} per month, and further usage can be purchased.`
                : `The trial includes ${money(status.trial_cap_cent)} of AI per month.`)}
          </p>
          {isOwner && kaufwegSichtbar() && (
            <Link to="/billing/setup">
              <Button size="sm" variant={status.over_included ? 'primary' : 'secondary'}>
                {de ? 'Test beenden und Abo starten' : 'End trial and start subscription'}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-xs text-muted">
            {de
              ? `Ist der Inklusivbetrag aufgebraucht, ruhen die KI-Funktionen bis zum ${resetLabel}. Sie können stattdessen weiterarbeiten — der Mehrverbrauch wird dann über Ihre AUVISO-Rechnung abgerechnet.`
              : `Once the included amount is used up, the AI features pause until ${resetLabel}. You can choose to keep working instead — anything beyond is billed on your AUVISO invoice.`}
          </p>
          {isOwner && !asking && (
            <Button
              size="sm"
              variant={status.over_included ? 'primary' : 'secondary'}
              disabled={busy || !status.billable}
              onClick={() => setAsking(true)}
            >
              {de ? 'Weiterarbeiten erlauben' : 'Allow continued use'}
            </Button>
          )}
          {/* Die Zustimmung wird ausdrücklich gegeben, mit dem Satz davor, dem
              sie gilt. Ein einzelner Klick auf einen Knopf mit dem Wort
              „erlauben" ist keine ausdrückliche Zustimmung. */}
          {isOwner && asking && (
            <div className="rounded-xl bg-surface-2 px-3 py-2.5 space-y-2 text-xs">
              <p>
                {de
                  ? `Ich stimme zu, dass KI-Nutzung über ${money(status.cap_cent)} je Monat hinaus über mein hinterlegtes Lastschriftmandat nachverrechnet wird. Der Verbrauch ist jederzeit auf dieser Seite einsehbar, und ich kann die Zustimmung jederzeit widerrufen.`
                  : `I agree that AI usage beyond ${money(status.cap_cent)} per month is billed via the direct debit mandate on file. Usage is visible on this page at any time, and I can withdraw this consent at any time.`}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="primary" disabled={busy} onClick={() => setConsent(true)}>
                  {de ? 'Zustimmen' : 'Agree'}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAsking(false)}>
                  {de ? 'Abbrechen' : 'Cancel'}
                </Button>
              </div>
            </div>
          )}
          {isOwner && !status.billable && (
            <p className="text-xs text-warn flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                {de
                  ? 'Dafür muss zuerst ein Zahlungsmittel hinterlegt sein.'
                  : 'A payment method must be on file first.'}
              </span>
            </p>
          )}
          {!isOwner && (
            <p className="text-xs text-muted">
              {de
                ? 'Diese Entscheidung kann nur der Inhaber des Betriebs treffen.'
                : 'Only the account owner can make this decision.'}
            </p>
          )}
        </div>
      )}

      <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
        <p className="text-xs text-muted">
          {/* Dieselbe Zurückhaltung wie oben in Zeile 305: In der Hülle bleibt der
              Inklusivbetrag stehen, aber NICHT der Betrag „ab dem bezahlten Abo".
              Eine Preisangabe zu einem Abo, das man in der App nicht kaufen kann,
              setzt einen anderen Kaufweg voraus — und genau das fassen Apple
              (3.1.3, „encourage users to use a purchasing method other than
              in-app purchase") und Google („may not lead users to a payment
              method other than Google Play's billing system … messaging")
              als Verstoß.

              GEFUNDEN AM 03.08.2026 DURCH AUSFÜHREN, NICHT DURCH LESEN: Die
              Stelle oben war geschützt, diese nicht — im Emulator sichtbar als
              „Im Test € 1,00 je Monat, ab dem bezahlten Abo € 3,00". Zwei Sätze
              mit demselben Inhalt in derselben Datei, einer bewacht, einer
              nicht. Wer nur `kaufwegSichtbar` grept, findet die Datei und hält
              sie für erledigt. */}
          {status.in_trial
            ? (de
              ? (kaufwegSichtbar()
                ? `Im Test ${money(status.trial_cap_cent)} je Monat, ab dem bezahlten Abo ${money(status.paid_cap_cent)}. Festgelegt von AUVISO.`
                // OHNE den Betrag: Die Zeile darueber nennt ihn bereits, und in
                // der Huelle standen dadurch zwei Saetze mit derselben Aussage
                // untereinander. Die eigene Aussage dieser Zeile ist nicht der
                // Betrag, sondern WER ihn festlegt.
                : 'Festgelegt von AUVISO.')
              : (kaufwegSichtbar()
                ? `Trial: ${money(status.trial_cap_cent)} per month; paid plan: ${money(status.paid_cap_cent)}. Set by AUVISO.`
                : 'Set by AUVISO.'))
            : (de
              ? 'Der Inklusivbetrag ist Teil Ihres Tarifs und wird von AUVISO festgelegt.'
              : 'The included amount is part of your plan and is set by AUVISO.')}
        </p>
        <Button size="sm" variant="ghost" onClick={() => void load()} disabled={busy}>
          <RotateCw size={14} />
        </Button>
      </div>

      {error && <p className="text-xs text-danger">{error}</p>}
    </Card>
  )
}
